// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import StorageHealth from "./components/OfflinePage/StorageHealth";
import { PRICING_PAGE_ENABLED } from "./consts/featureFlags"
import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react"
import { Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Helmet } from "react-helmet-async"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faKeyboard } from "@fortawesome/free-solid-svg-icons"
import "react-toggle/style.css"
import { hiraganas } from "./consts/hiraganaList"
import { katakana } from "./consts/katakana"
import Header from "./components/header/HeaderComponent"
import TextAreaBox from "./components/ui/text-area/TextAreaBoxComponent"
import KanjiSuggestion from "./components/kanji-suggestion/KanjiSuggestionComponent"
import ToggleButton from "./components/ui/toogle-button/ToggleButtonComponent"
import CharacterList from "./components/character-list/CharacterListComponent"
import ConverterPage from "./components/converterPage/ConverterPage"
import RomajiConverterPage from "./components/romajiConverterPage/RomajiConverterPage"
import Footer from "./components/footer/FooterComponent"
import Seo from "./components/Seo/SeoComponent"
import UpdateAvailableBanner from "./components/UpdateAvailableBanner/UpdateAvailableBanner"
import SocialSignInErrorRedirect from "./components/SocialSignInErrorRedirect/SocialSignInErrorRedirect"
import DeviceProgressStatus from "./components/DeviceProgressStatus/DeviceProgressStatus"
import ExampleSuggestions from "./components/example-suggestions/ExampleSuggestionsComponent"
import { hiraganaExamplePhrases, katakanaExamplePhrases } from "./consts/examplePhrases"
import { buildRomajiMap, convertRomajiValue, getRomajiIdiomCorrection } from "./utils/romajiConverter"
import styles from "./app.module.scss"
import ReleasesInfoPage from "./components/ReleasesInfoPage/ReleasesInfoPage"
import PrivacyPolicyPage from "./components/PrivacyPolicyPage/PrivacyPolicyPage"
import CreditsPage from "./components/CreditsPage/CreditsPage"
import AboutPage from "./components/AboutPage/AboutPage"
import NotFoundPage from "./components/NotFoundPage/NotFoundPage"
import HomePage from "./components/HomePage/HomePage"
import StudyMaterialsPage from "./components/StudyMaterialsPage/StudyMaterialsPage"
import PageLoader from "./components/PageLoader/PageLoaderComponent"
import LanguageRoute from "./components/LanguageRoute/LanguageRoute"
import { trackPageVisit } from "./utils/visitTracking"
import * as analytics from "./utils/analytics"
import { getKanjiSuggestionsFromHiragana } from "./utils/suggestionsEngine"
import { SavedWordsProvider } from "./components/SavedWords/SavedWordsProvider"
import SavedWordsPage from "./components/SavedWords/SavedWordsPage"
import DesktopUnavailable from "./components/DesktopUnavailable"
import { isDesktopLocalProfile } from "./utils/desktopProfile"
import { languageForPath } from "./utils/documentLanguage"

// Lazy-loaded since it pulls in the full grammarPoints content (used just to
// find a couple of specific points and read its length) into the main
// bundle otherwise.
const LessonsPage = lazy(() => import("./components/LessonsPage/LessonsPage"))
// Lazy-loaded since it pulls in readings/examples data for all 2,136 jōyō
// kanji, which would otherwise bloat the main bundle for every visitor.
const KanjiDetailPage = lazy(() => import("./components/KanjiDetailPage/KanjiDetailPage"))
const PtBlogPostPage = lazy(() => import("./components/BlogPostPage/PtBlogPostPage"))
const BlogPage = lazy(() => import("./components/BlogPage/BlogPage"))
const BlogPostPage = lazy(() => import("./components/BlogPostPage/BlogPostPage"))
// Lazy-loaded for the same reason (readings/meanings for quiz generation).
const WanikaniPage = lazy(() => import("./components/WanikaniPage/WanikaniPage"))
// Lazy-loaded since it pulls in meanings for all 2,974 jōyō and jinmeiyō kanji.
const KanjiRoulettePage = lazy(() => import("./components/KanjiRoulettePage/KanjiRoulettePage"))
const KanjiMemoryQuizPage = lazy(() => import("./components/KanjiMemoryQuizPage/KanjiMemoryQuizPage"))
const SimilarKanjiPage = lazy(() => import("./components/SimilarKanjiPage/SimilarKanjiPage"))
const SimilarKanjiQuizPage = lazy(() => import("./components/SimilarKanjiPage/SimilarKanjiQuizPage"))
const KanjiMathematicsPage = lazy(() => import("./components/KanjiMathematicsPage/KanjiMathematicsPage"))
const ReadingPracticePage = lazy(() => import("./components/ReadingPracticePage/ReadingPracticePage"))
const N5ExamGuidePage = lazy(() => import("./components/N5ExamGuidePage/N5ExamGuidePage"))
const LearningRoadmapPage = lazy(() => import("./components/LearningRoadmapPage/LearningRoadmapPage"))
const PracticePage = lazy(() => import("./components/PracticePage/PracticePage"))
const LearnPage = lazy(() => import("./components/LearnPage/LearnPage"))
const KanjiGuessQuizPage = lazy(() => import("./components/KanjiGuessQuizPage/KanjiGuessQuizPage"))
const NumbersPage = lazy(() => import("./components/NumbersPage/NumbersPage"))
const CurrencyConverterPage = lazy(() => import("./components/CurrencyConverterPage/CurrencyConverterPage"))
const CurrencyCheatSheetPage = lazy(() => import("./components/CurrencyConverterPage/CurrencyCheatSheetPage"))
// Lazy-loaded since it pulls in the 214-radical dataset and its PT-PT text (~115 kB of source).
const RadicalsPage = lazy(() => import("./components/RadicalsPage/RadicalsPage"))
// Lazy-loaded since it pulls in the hanzi-writer library for stroke animation.
const KanaWritingPracticePage = lazy(() => import("./components/KanaWritingPracticePage/KanaWritingPracticePage"))
// Lazy-loaded since it pulls in hanzi-writer plus meanings for all 2,974 jōyō and jinmeiyō kanji.
const KanjiWritingPracticePage = lazy(() => import("./components/KanjiWritingPracticePage/KanjiWritingPracticePage"))
// Lazy-loaded since selecting an image dynamically pulls in the tesseract.js OCR library.
const WorksheetGeneratorPage = lazy(() => import("./components/WorksheetGeneratorPage/WorksheetGeneratorPage"))
const KanjiUsagePrintablePage = lazy(() => import("./components/KanjiUsagePrintablePage/KanjiUsagePrintablePage"))
// Unlisted Premium pricing/checkout page - no nav/header/footer link points here, see PricingPage.tsx.
const PricingPage = lazy(() => import("./components/PricingPage/PricingPage"))
// Real Stripe Checkout went live on /pricing before the EU consumer-law
// review (docs/legal/2026-10-13-stripe-premium-launch-review.md) was
// addressed - missing Terms of Service, withdrawal-right consent, operator
// identity, VAT handling. Kill switch while that's sorted: both /pricing
// routes below render NotFoundPage instead, same as any nonexistent path,
// rather than the real checkout UI. Flip to true once the legal page/
// consent work actually lands - do not flip this without that.
// Moved to src/consts/featureFlags.ts so the footer link is gated by the same
// flag as the route - see the comment there before flipping it.
const TranslatePage = lazy(() => import("./components/TranslatePage/TranslatePage"))
const SpeakingPracticePage = lazy(() => import("./components/SpeakingPracticePage/SpeakingPracticePage"))
const GrammarPage = lazy(() => import("./components/GrammarPage/GrammarPage"))
const GrammarDetailPage = lazy(() => import("./components/GrammarDetailPage/GrammarDetailPage"))
const VocabularyPage = lazy(() => import("./components/VocabularyPage/VocabularyPage"))
const ConjugationPage = lazy(() => import("./components/ConjugationPage/ConjugationPage"))
const AdjectiveConjugationPage = lazy(() => import("./components/AdjectiveConjugationPage/AdjectiveConjugationPage"))
// Lazy-loaded since these pull in the aws-amplify auth library, unneeded by anonymous visitors.
const LoginPage = lazy(() => import("./components/LoginPage/LoginPage"))
const RegisterPage = lazy(() => import("./components/RegisterPage/RegisterPage"))
const ForgotPasswordPage = lazy(() => import("./components/ForgotPasswordPage/ForgotPasswordPage"))
const TwitterAuthFinishPage = lazy(() => import("./components/TwitterAuthFinishPage/TwitterAuthFinishPage"))
const ProfilePage = lazy(() => import("./components/ProfilePage/ProfilePage"))
const OfflinePage = lazy(() => import("./components/OfflinePage/OfflinePage"));
const StatsPage = lazy(() => import("./components/StatsPage/StatsPage"))
const AgentsPage = lazy(() => import("./components/AgentsPage/AgentsPage"))
const DesktopAppPage = lazy(() => import("./components/DesktopAppPage/DesktopAppPage"))
const NewsletterPage = lazy(() => import("./components/NewsletterPage/NewsletterPage"))
const UnsubscribePage = lazy(() => import("./components/NewsletterPage/UnsubscribePage"))
const NewsletterConfirmPage = lazy(() => import("./components/NewsletterPage/ConfirmPage"))
const RequireAuth = lazy(() => import("./components/RequireAuth/RequireAuth"))
const RequireAdmin = lazy(() => import("./components/RequireAdmin/RequireAdmin"))
const RedirectIfAuthenticated = lazy(() => import("./components/RequireAuth/RedirectIfAuthenticated"))
const AssistantWidget = lazy(() => import("./components/AssistantWidget/AssistantWidget"))
const JlptTestSitesPage = lazy(() => import("./components/JlptTestSitesPage/JlptTestSitesPage"))
const SentryTestPage = lazy(() => import("./components/SentryTestPage/SentryTestPage"))
const FeatureGate = lazy(() => import("./components/FeatureGate/FeatureGate"))

function KeyboardPage() {
  const { t, i18n } = useTranslation("keyboardPage")
  const [searchParams] = useSearchParams()
  const [newWord, setNewWord] = useState("")
  const [phrase, setPhrase] = useState("")
  const [useKeyboard, setUseKeyboard] = useState(true)
  const [isClipboardDisabled, setIsClipboardDisabled] = useState(true)
  const [copyError, setCopyError] = useState(false)
  const [selectedKeyboard, setSelectedKeyboard] = useState(
    searchParams.get("script") === "katakana" ? "katakana" : "hiragana"
  )
  const [romajiBuffer, setRomajiBuffer] = useState("")
  const [typedRomajiWord, setTypedRomajiWord] = useState("")
  const [highlightKey, setHighlightKey] = useState("")
  const [isDesktop, setIsDesktop] = useState(false)
  const [lastProduced, setLastProduced] = useState(null)
  const keyboardRef = useRef(null)
  const lastProducedTimeoutRef = useRef(null)
  const pendingNTimeoutRef = useRef(null)
  const hiraganaTabRef = useRef(null)
  const katakanaTabRef = useRef(null)

  // A lone "n" typed live is ambiguous - it might be the whole word ("hon"
  // -> ほん) or the first half of a doubled "nn" (e.g. "konnichiwa").
  // convertRomajiValue is called with commitTrailingN: false so it waits
  // instead of guessing; this is how long it waits before assuming no more
  // input is coming and committing the pending "n" to ん on its own.
  const PENDING_N_COMMIT_DELAY_MS = 500

  useEffect(() => {
    const updateDesktop = () => setIsDesktop(window.innerWidth >= 1100)
    updateDesktop()
    window.addEventListener("resize", updateDesktop)
    return () => window.removeEventListener("resize", updateDesktop)
  }, [])

  useEffect(() => {
    return () => {
      clearTimeout(lastProducedTimeoutRef.current)
      clearTimeout(pendingNTimeoutRef.current)
    }
  }, [])

  function clearPendingN() {
    clearTimeout(pendingNTimeoutRef.current)
    pendingNTimeoutRef.current = null
  }

  function handleChange(char) {
    clearPendingN()
    setNewWord(newWord + char)
    setTypedRomajiWord("")
    setIsClipboardDisabled(true)
  }

  const currentRomajiMap = useMemo(() => {
    const characters = selectedKeyboard === "hiragana" ? hiraganas : katakana;
    return buildRomajiMap(characters);
  }, [selectedKeyboard]);

  const wordCorrection = useMemo(() => getRomajiIdiomCorrection(typedRomajiWord), [typedRomajiWord]);

  function commitPendingN(isKatakana) {
    const kana = isKatakana ? "ン" : "ん";
    setPhrase((p) => p + kana);
    setNewWord((n) => n + kana);
    setTypedRomajiWord((w) => w + "n");
    setIsClipboardDisabled(false);
    setRomajiBuffer("");
    setLastProduced({ kana, romaji: "n" });
    clearTimeout(lastProducedTimeoutRef.current);
    lastProducedTimeoutRef.current = setTimeout(() => setLastProduced(null), 1100);
    pendingNTimeoutRef.current = null;
  }

  function handleRomajiChange(value) {
    const normalizedValue = String(value || "").toLowerCase().replace(/\s+/g, "");
    setHighlightKey(normalizedValue);
    clearPendingN();

    const isKatakana = selectedKeyboard === "katakana";
    const { produced, remainder } = convertRomajiValue(normalizedValue, currentRomajiMap, isKatakana, false);
    if (produced) {
      setPhrase((p) => p + produced);
      setIsClipboardDisabled(false);
      setNewWord((n) => n + produced);

      const consumedRomaji = normalizedValue.slice(0, normalizedValue.length - remainder.length);
      setTypedRomajiWord((w) => w + consumedRomaji);
      setLastProduced({ kana: produced, romaji: consumedRomaji });
      clearTimeout(lastProducedTimeoutRef.current);
      lastProducedTimeoutRef.current = setTimeout(() => setLastProduced(null), 1100);
    }
    setRomajiBuffer(remainder);

    if (remainder === "n") {
      pendingNTimeoutRef.current = setTimeout(() => commitPendingN(isKatakana), PENDING_N_COMMIT_DELAY_MS);
    }
  }

  function handleExampleSelect(value) {
    clearPendingN()
    setPhrase(phrase + value)
    setNewWord("")
    setTypedRomajiWord("")
    setIsClipboardDisabled(false)
  }

  function handleSelectedWord(word) {
    const resultCount = getKanjiSuggestionsFromHiragana(newWord).length
    analytics.track("dictionary_search", {
      found: resultCount > 0,
      resultCount,
      queryLength: newWord.length,
      selectedSuggestion: word !== newWord,
    })
    clearPendingN()
    setPhrase(phrase + word)
    setNewWord("")
    setTypedRomajiWord("")
    setIsClipboardDisabled(false)
  }

  function handleToggleKeyboard() {
    setUseKeyboard(!useKeyboard)
  }

  function handleKeyboardTabKeyDown(event) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return
    event.preventDefault()
    const next = selectedKeyboard === "hiragana" ? "katakana" : "hiragana"
    setSelectedKeyboard(next)
    const nextTabRef = next === "hiragana" ? hiraganaTabRef : katakanaTabRef
    nextTabRef.current?.focus()
  }

  function handleCancelSuggestion() {
    const resultCount = getKanjiSuggestionsFromHiragana(newWord).length
    analytics.track("dictionary_search", {
      found: resultCount > 0,
      resultCount,
      queryLength: newWord.length,
      selectedSuggestion: false,
    })
    setNewWord("")
    setTypedRomajiWord("")
  }

  function handleOnDelete() {
    clearPendingN()
    const updated = phrase.slice(0, -1)
    setPhrase(updated)
    setIsClipboardDisabled(updated.length === 0)
  }

  function handleDeleteAll() {
    clearPendingN()
    setPhrase("")
    setNewWord("")
    setTypedRomajiWord("")
    setRomajiBuffer("")
    setIsClipboardDisabled(true)
  }

  function handleCopyToClipboard() {
    navigator.clipboard
      .writeText(phrase)
      .then(() => {
        setCopyError(false)
        setIsClipboardDisabled(true)
      })
      .catch(() => setCopyError(true))
  }

  function handleScrollToKeyboard() {
    keyboardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const isPt = i18n.language === "pt"
  const currentPath = isPt ? "/pt/keyboard" : "/keyboard"

  return (
    <div className={styles.page}>
      <Seo
        title={t("seoTitle")}
        description={t("seoDescription")}
        path={currentPath}
        lang={isPt ? "pt-PT" : undefined}
      />
      <Header />

      <main className={styles.main}>
        <div className={styles.pageLayout} data-testid="keyboard-page-layout">
          <section className={styles.editor}>
            <div className={styles.romajiField}>
              {romajiBuffer && (
                <div className={styles.romajiBubble} role="status" aria-live="polite">
                  <span className={styles.romajiBubbleTyping}>{romajiBuffer}</span>
                </div>
              )}
              {!romajiBuffer && lastProduced && (
                <div
                  className={`${styles.romajiBubble} ${styles.romajiBubbleProduced}`}
                  role="status"
                  aria-live="polite"
                >
                  <span className={styles.romajiBubbleKana}>{lastProduced.kana}</span>
                  <span className={styles.romajiBubbleRomaji}>{lastProduced.romaji}</span>
                </div>
              )}
              <label htmlFor="romaji-input" className={styles.romajiLabel}>
                <FontAwesomeIcon icon={faKeyboard} className={styles.romajiLabelIcon} />
                {t("typeHereRomaji")}
              </label>
              <input
                id="romaji-input"
                className={styles.romajiInput}
                value={romajiBuffer}
                onChange={(e) => handleRomajiChange(e.target.value)}
                placeholder={t("romajiPlaceholder")}
                aria-label={t("romajiInputAriaLabel")}
              />
              <div className={styles.romajiPreview}>
                <span className={styles.romajiPreviewLabel}>{t("pending")}</span>
                <span className={styles.romajiPreviewValue}>
                  {romajiBuffer ? romajiBuffer : t("noUnmatchedRomaji")}
                </span>
              </div>
              {wordCorrection && newWord && (
                <p className={styles.romajiCorrectionHint} role="status">
                  {t("didYouMean")} <span className={styles.romajiCorrectionKana}>{wordCorrection.correct}</span>?{" "}
                  {wordCorrection.note}
                </p>
              )}
            </div>

            <div className={styles.areaField}>
              <label htmlFor="phrase-output" className={styles.areaLabel}>
                {t("yourText")}
              </label>
              <TextAreaBox
                id="phrase-output"
                classname={styles.area}
                value={phrase}
                placeholder={t("areaPlaceholder")}
                onDeletePressed={handleOnDelete}
              />
            </div>

            <div className={styles.buttonRow}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={handleOnDelete}
                disabled={phrase === ""}
              >
                {t("delete")}
              </button>

              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={handleDeleteAll}
                disabled={phrase === "" && newWord === ""}
              >
                {t("deleteAll")}
              </button>

              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={handleCopyToClipboard}
                disabled={isClipboardDisabled}
              >
                {t("copyToClipboard")}
              </button>
            </div>

            {copyError && (
              <p className={styles.copyErrorText} role="alert">
                {t("copyError")}
              </p>
            )}

            {!newWord && (
              <ExampleSuggestions
                examples={selectedKeyboard === "katakana" ? katakanaExamplePhrases : hiraganaExamplePhrases}
                onSelect={handleExampleSelect}
              />
            )}

                <div className={styles.kanjiContainer}>
              {(newWord || (highlightKey && currentRomajiMap[highlightKey])) ? (
                <KanjiSuggestion
                  data-testid="app-kanji-suggestion"
                  word={newWord || currentRomajiMap[highlightKey]}
                  selectedWordHandler={handleSelectedWord}
                  cancelHandler={handleCancelSuggestion}
                />
              ) : (
                <div className={styles.genericTutorialContainer}>
                  <h3 className={styles.tutorialTitle}>{t("howToUse")}</h3>
                  <div className={styles.step}>
                    {t("step1")}
                  </div>
                  <div className={styles.step}>
                    {t("step2")}
                  </div>
                  <div className={styles.step}>
                    {t("step3")}
                  </div>
                </div>
              )}
            </div>

            {!isDesktop && (
              <div className={styles.toggleRow}>
                <ToggleButton
                  label={t("showKeyboards")}
                  className={styles.toggle}
                  value={useKeyboard}
                  showIcons={false}
                  onChange={handleToggleKeyboard}
                />
              </div>
            )}

            {!isDesktop && useKeyboard && (
              <button
                type="button"
                className={styles.scrollHint}
                onClick={handleScrollToKeyboard}
              >
                <span className={styles.scrollHintText}>{t("scrollHint")}</span>
                <span className={styles.scrollArrow}>↓</span>
              </button>
            )}
          </section>

          <div className={styles.keyboardColumn}>
            {(isDesktop || useKeyboard) && (
              <section ref={keyboardRef} className={styles.keyboardSection}>
                <div className={styles.keyboardHeader}>
                  <div
                    className={styles.keyboardTabs}
                    role="tablist"
                    onKeyDown={handleKeyboardTabKeyDown}
                  >
                    <button
                      type="button"
                      id="keyboard-tab-hiragana"
                      role="tab"
                      ref={hiraganaTabRef}
                      aria-selected={selectedKeyboard === "hiragana"}
                      aria-controls="keyboard-tabpanel"
                      tabIndex={selectedKeyboard === "hiragana" ? 0 : -1}
                      className={`${styles.keyboardTab} ${selectedKeyboard === "hiragana" ? styles.keyboardTabActive : ""}`}
                      onClick={() => setSelectedKeyboard("hiragana")}
                    >
                      {t("hiragana")}
                    </button>
                    <button
                      type="button"
                      id="keyboard-tab-katakana"
                      role="tab"
                      ref={katakanaTabRef}
                      aria-selected={selectedKeyboard === "katakana"}
                      aria-controls="keyboard-tabpanel"
                      tabIndex={selectedKeyboard === "katakana" ? 0 : -1}
                      className={`${styles.keyboardTab} ${selectedKeyboard === "katakana" ? styles.keyboardTabActive : ""}`}
                      onClick={() => setSelectedKeyboard("katakana")}
                    >
                      {t("katakana")}
                    </button>
                  </div>
                </div>

                <div
                  id="keyboard-tabpanel"
                  role="tabpanel"
                  aria-labelledby={selectedKeyboard === "hiragana" ? "keyboard-tab-hiragana" : "keyboard-tab-katakana"}
                >
                  <CharacterList
                    data-testid={selectedKeyboard === "hiragana" ? "app-hiragana-list" : "app-katakana-list"}
                    characters={selectedKeyboard === "hiragana" ? hiraganas : katakana}
                    rows={6}
                    handleChange={handleChange}
                    highlightKey={highlightKey}
                  />
                </div>
              </section>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

// Offline desktop build only (see utils/desktopProfile.ts): a page that needs the internet shows the
// shared "not available" notice instead. On the website the flag is false and this just renders the page.
function OnlineOnly({ feature, children }) {
  return isDesktopLocalProfile ? <DesktopUnavailable feature={feature} /> : children
}

function PageViewTracker() {
  const location = useLocation()

  useEffect(() => {
    trackPageVisit(location.pathname)
    analytics.page(location.pathname)
  }, [location.pathname])

  return null
}

// react-helmet-async removes an attribute when the last <Helmet> that set it unmounts, so with only the
// per-page <Seo> setting <html lang>, moving to a page without a <Seo> (or through a lazy route's
// loading fallback) left the attribute empty. This one never unmounts, and it mounts before any page, so
// a page's own <Seo lang> (mounted later, hence "innermost") still overrides it - which is what keeps a
// standalone Portuguese blog post at /blog/<ptSlug> pt-PT. A component of its own rather than a hook
// call in App(), so a route change doesn't re-render the rest of App's chrome.
function DocumentLanguage() {
  const { pathname } = useLocation()

  return <Helmet htmlAttributes={{ lang: languageForPath(pathname) }} />
}

function App() {
  return (
    <>
      <DocumentLanguage />
      <PageViewTracker />
      <SocialSignInErrorRedirect />
      <UpdateAvailableBanner />
      <StorageHealth /><DeviceProgressStatus />
      <Suspense fallback={null}>
        <AssistantWidget />
      </Suspense>
      <Routes>
        <Route path="/login/stats" element={<Navigate to="/stats" replace />} />
        <Route path="*" element={<NotFoundPage />} />
        <Route path="/" element={<HomePage />} />
        <Route
          path="/pt"
          element={
            <LanguageRoute lang="pt">
              <HomePage />
            </LanguageRoute>
          }
        />
        <Route
          path="/sentry-test"
          element={
            <Suspense fallback={<PageLoader />}>
              <RequireAdmin>
                <SentryTestPage />
              </RequireAdmin>
            </Suspense>
          }
        />
        <Route path="/offline" element={<Suspense fallback={<PageLoader />}><OnlineOnly feature="offlinePacks"><OfflinePage /></OnlineOnly></Suspense>} />
        <Route path="/pt/offline" element={<Suspense fallback={<PageLoader />}><LanguageRoute lang="pt"><OnlineOnly feature="offlinePacks"><OfflinePage /></OnlineOnly></LanguageRoute></Suspense>} />
        <Route path="/keyboard" element={<KeyboardPage />} />
        <Route
          path="/pt/keyboard"
          element={
            <LanguageRoute lang="pt">
              <KeyboardPage />
            </LanguageRoute>
          }
        />
        <Route
          path="/lessons"
          element={
            <Suspense fallback={<PageLoader />}>
              <SavedWordsProvider><LessonsPage /></SavedWordsProvider>
            </Suspense>
          }
        />
        <Route path="/saved-words" element={<SavedWordsPage />} />
        <Route path="/pt/saved-words" element={<LanguageRoute lang="pt"><SavedWordsPage /></LanguageRoute>} />
        <Route
          path="/pt/lessons"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <SavedWordsProvider><LessonsPage /></SavedWordsProvider>
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/blog"
          element={
            <Suspense fallback={<PageLoader />}>
              <BlogPage />
            </Suspense>
          }
        />
        <Route
          path="/pt/blog"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <BlogPage />
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/blog/:slug"
          element={
            <Suspense fallback={<PageLoader />}>
              <SavedWordsProvider><BlogPostPage /></SavedWordsProvider>
            </Suspense>
          }
        />
        <Route
          path="/pt/blog/:slug"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <SavedWordsProvider><PtBlogPostPage /></SavedWordsProvider>
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/kanji/:symbol"
          element={
            <Suspense fallback={<PageLoader />}>
              <KanjiDetailPage />
            </Suspense>
          }
        />
        <Route
          path="/pt/kanji/:symbol"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <KanjiDetailPage />
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/grammar"
          element={
            <Suspense fallback={<PageLoader />}>
              <GrammarPage />
            </Suspense>
          }
        />
        <Route
          path="/pt/grammar"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <GrammarPage />
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/grammar/:id"
          element={
            <Suspense fallback={<PageLoader />}>
              <GrammarDetailPage />
            </Suspense>
          }
        />
        <Route
          path="/pt/grammar/:id"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <GrammarDetailPage />
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/vocabulary"
          element={
            <Suspense fallback={<PageLoader />}>
              <VocabularyPage />
            </Suspense>
          }
        />
        <Route
          path="/pt/vocabulary"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <VocabularyPage />
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/conjugation"
          element={
            <Suspense fallback={<PageLoader />}>
              <ConjugationPage />
            </Suspense>
          }
        />
        <Route
          path="/pt/conjugation"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <ConjugationPage />
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/adjective-conjugation"
          element={
            <Suspense fallback={<PageLoader />}>
              <SavedWordsProvider><AdjectiveConjugationPage /></SavedWordsProvider>
            </Suspense>
          }
        />
        <Route
          path="/pt/adjective-conjugation"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <SavedWordsProvider><AdjectiveConjugationPage /></SavedWordsProvider>
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route path="/converter" element={<ConverterPage />} />
        <Route
          path="/pt/converter"
          element={
            <LanguageRoute lang="pt">
              <ConverterPage />
            </LanguageRoute>
          }
        />
        <Route path="/romaji-converter" element={<RomajiConverterPage />} />
        <Route
          path="/pt/romaji-converter"
          element={
            <LanguageRoute lang="pt">
              <RomajiConverterPage />
            </LanguageRoute>
          }
        />
        <Route path="/releases" element={<ReleasesInfoPage />} />
        <Route
          path="/pt/releases"
          element={
            <LanguageRoute lang="pt">
              <ReleasesInfoPage />
            </LanguageRoute>
          }
        />
        <Route
          path="/newsletter"
          element={
            <Suspense fallback={<PageLoader />}>
              <NewsletterPage />
            </Suspense>
          }
        />
        <Route
          path="/pt/newsletter"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <NewsletterPage />
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/newsletter/confirm"
          element={
            <Suspense fallback={<PageLoader />}>
              <NewsletterConfirmPage />
            </Suspense>
          }
        />
        <Route
          path="/newsletter/unsubscribe"
          element={
            <Suspense fallback={<PageLoader />}>
              <UnsubscribePage />
            </Suspense>
          }
        />
        <Route path="/about" element={<AboutPage />} />
        <Route
          path="/pt/about"
          element={
            <LanguageRoute lang="pt">
              <AboutPage />
            </LanguageRoute>
          }
        />
        <Route path="/study-materials" element={<StudyMaterialsPage />} />
        <Route
          path="/pt/study-materials"
          element={
            <LanguageRoute lang="pt">
              <StudyMaterialsPage />
            </LanguageRoute>
          }
        />
        <Route
          path="/jlpt-test-sites"
          element={
            <Suspense fallback={<PageLoader />}>
              <JlptTestSitesPage />
            </Suspense>
          }
        />
        <Route
          path="/jlpt"
          element={
            <Suspense fallback={<PageLoader />}>
              <JlptTestSitesPage />
            </Suspense>
          }
        />
        <Route
          path="/jlpt/n5-exam-preparation"
          element={
            <Suspense fallback={<PageLoader />}>
              <SavedWordsProvider><N5ExamGuidePage /></SavedWordsProvider>
            </Suspense>
          }
        />
        <Route
          path="/pt/jlpt/n5-exam-preparation"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt"><SavedWordsProvider><N5ExamGuidePage /></SavedWordsProvider></LanguageRoute>
            </Suspense>
          }
        />
        {/* PT-PT pilot (see docs/market-research/portuguese-market.md): only
            this page has real translations right now, so it's routed
            individually rather than via a site-wide /pt prefix. */}
        <Route
          path="/pt/jlpt-test-sites"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <JlptTestSitesPage />
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/pt/jlpt"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <JlptTestSitesPage />
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route
          path="/pt/privacy-policy"
          element={
            <LanguageRoute lang="pt">
              <PrivacyPolicyPage />
            </LanguageRoute>
          }
        />
        <Route path="/credits" element={<CreditsPage />} />
        <Route
          path="/pt/credits"
          element={
            <LanguageRoute lang="pt">
              <CreditsPage />
            </LanguageRoute>
          }
        />
        <Route
          path="/stats"
          element={
            <Suspense fallback={<PageLoader />}>
              <RequireAdmin>
                <StatsPage />
              </RequireAdmin>
            </Suspense>
          }
        />
        <Route
          path="/agents"
          element={
            <Suspense fallback={<PageLoader />}>
              <RequireAdmin>
                <AgentsPage />
              </RequireAdmin>
            </Suspense>
          }
        />
        <Route
          path="/desktop-app"
          element={
            <Suspense fallback={<PageLoader />}>
              <RequireAdmin>
                <DesktopAppPage />
              </RequireAdmin>
            </Suspense>
          }
        />
        <Route
          path="/pt/desktop-app"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <RequireAdmin>
                  <DesktopAppPage />
                </RequireAdmin>
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/wanikani"
          element={
            <Suspense fallback={<PageLoader />}>
              <OnlineOnly feature="wanikani"><WanikaniPage /></OnlineOnly>
            </Suspense>
          }
        />
        <Route
          path="/pt/wanikani"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <OnlineOnly feature="wanikani"><WanikaniPage /></OnlineOnly>
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route path="/kanji-roulette" element={<Suspense fallback={<PageLoader />}><KanjiRoulettePage /></Suspense>} />
        <Route path="/pt/kanji-roulette" element={<Suspense fallback={<PageLoader />}><LanguageRoute lang="pt"><KanjiRoulettePage /></LanguageRoute></Suspense>} />
        <Route
          path="/kanji-memory-quiz"
          element={
            <Suspense fallback={<PageLoader />}>
              <FeatureGate featureId="kanji-memory-quiz"><KanjiMemoryQuizPage /></FeatureGate>
            </Suspense>
          }
        />
        <Route path="/similar-kanji" element={<Suspense fallback={<PageLoader />}><SimilarKanjiPage /></Suspense>} />
        <Route path="/pt/similar-kanji" element={<Suspense fallback={<PageLoader />}><LanguageRoute lang="pt"><SimilarKanjiPage /></LanguageRoute></Suspense>} />
        <Route path="/similar-kanji/quiz" element={<Suspense fallback={<PageLoader />}><SimilarKanjiQuizPage /></Suspense>} />
        <Route path="/pt/similar-kanji/quiz" element={<Suspense fallback={<PageLoader />}><LanguageRoute lang="pt"><SimilarKanjiQuizPage /></LanguageRoute></Suspense>} />
        <Route path="/kanji-mathematics" element={<Suspense fallback={<PageLoader />}><KanjiMathematicsPage /></Suspense>} />
        <Route path="/pt/kanji-mathematics" element={<Suspense fallback={<PageLoader />}><LanguageRoute lang="pt"><KanjiMathematicsPage /></LanguageRoute></Suspense>} />
        <Route path="/reading-practice" element={<Suspense fallback={<PageLoader />}><ReadingPracticePage /></Suspense>} />
        <Route path="/pt/reading-practice" element={<Suspense fallback={<PageLoader />}><LanguageRoute lang="pt"><ReadingPracticePage /></LanguageRoute></Suspense>} />
        <Route path="/learning-roadmap" element={<Suspense fallback={<PageLoader />}><LearningRoadmapPage /></Suspense>} />
        <Route path="/pt/learning-roadmap" element={<Suspense fallback={<PageLoader />}><LanguageRoute lang="pt"><LearningRoadmapPage /></LanguageRoute></Suspense>} />
        <Route path="/learn" element={<Suspense fallback={<PageLoader />}><LearnPage /></Suspense>} />
        <Route path="/pt/learn" element={<Suspense fallback={<PageLoader />}><LanguageRoute lang="pt"><LearnPage /></LanguageRoute></Suspense>} />
        <Route path="/practice" element={<Suspense fallback={<PageLoader />}><PracticePage /></Suspense>} />
        <Route path="/pt/practice" element={<Suspense fallback={<PageLoader />}><LanguageRoute lang="pt"><PracticePage /></LanguageRoute></Suspense>} />
        <Route
          path="/pt/kanji-memory-quiz"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <FeatureGate featureId="kanji-memory-quiz"><KanjiMemoryQuizPage /></FeatureGate>
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/kanji-quiz"
          element={
            <Suspense fallback={<PageLoader />}>
              <KanjiGuessQuizPage />
            </Suspense>
          }
        />
        <Route
          path="/kanji-quiz/:difficulty"
          element={
            <Suspense fallback={<PageLoader />}>
              <KanjiGuessQuizPage />
            </Suspense>
          }
        />
        <Route
          path="/pt/kanji-quiz"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <KanjiGuessQuizPage />
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/pt/kanji-quiz/:difficulty"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <KanjiGuessQuizPage />
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route path="/numbers" element={<Suspense fallback={<PageLoader />}><NumbersPage /></Suspense>} />
        <Route path="/pt/numbers" element={<Suspense fallback={<PageLoader />}><LanguageRoute lang="pt"><NumbersPage /></LanguageRoute></Suspense>} />
        <Route path="/currency-converter" element={<Suspense fallback={<PageLoader />}><CurrencyConverterPage /></Suspense>} />
        <Route path="/pt/currency-converter" element={<Suspense fallback={<PageLoader />}><LanguageRoute lang="pt"><CurrencyConverterPage /></LanguageRoute></Suspense>} />
        <Route path="/currency-cheat-sheet" element={<Suspense fallback={<PageLoader />}><CurrencyCheatSheetPage /></Suspense>} />
        <Route path="/pt/currency-cheat-sheet" element={<Suspense fallback={<PageLoader />}><LanguageRoute lang="pt"><CurrencyCheatSheetPage /></LanguageRoute></Suspense>} />
        <Route
          path="/radicals"
          element={
            <Suspense fallback={<PageLoader />}>
              <RadicalsPage />
            </Suspense>
          }
        />
        <Route
          path="/pt/radicals"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <RadicalsPage />
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/kana-writing-practice"
          element={
            <Suspense fallback={<PageLoader />}>
              <KanaWritingPracticePage />
            </Suspense>
          }
        />
        <Route
          path="/pt/kana-writing-practice"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <KanaWritingPracticePage />
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/kanji-writing-practice"
          element={
            <Suspense fallback={<PageLoader />}>
              <KanjiWritingPracticePage />
            </Suspense>
          }
        />
        <Route
          path="/pt/kanji-writing-practice"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <KanjiWritingPracticePage />
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/worksheets"
          element={
            <Suspense fallback={<PageLoader />}>
              <WorksheetGeneratorPage />
            </Suspense>
          }
        />
        <Route
          path="/pt/worksheets"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <WorksheetGeneratorPage />
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/worksheets/kanji-usage"
          element={
            <Suspense fallback={<PageLoader />}>
              <KanjiUsagePrintablePage />
            </Suspense>
          }
        />
        <Route
          path="/pt/worksheets/kanji-usage"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <KanjiUsagePrintablePage />
              </LanguageRoute>
            </Suspense>
          }
        />
        {/* Unlisted Premium pricing/checkout page (real Stripe Checkout, NIH-97) - deliberately
            not linked from any nav/header/footer/menu and excluded from public/sitemap.xml and
            robots.txt. See src/components/PricingPage/PricingPage.tsx. Behind PRICING_PAGE_ENABLED
            (see above) until the EU consumer-law review is addressed. */}
        <Route
          path="/pricing"
          element={
            PRICING_PAGE_ENABLED ? (
              <Suspense fallback={<PageLoader />}>
                <OnlineOnly feature="online"><PricingPage /></OnlineOnly>
              </Suspense>
            ) : (
              <NotFoundPage />
            )
          }
        />
        <Route
          path="/pt/pricing"
          element={
            PRICING_PAGE_ENABLED ? (
              <Suspense fallback={<PageLoader />}>
                <LanguageRoute lang="pt">
                  <OnlineOnly feature="online"><PricingPage /></OnlineOnly>
                </LanguageRoute>
              </Suspense>
            ) : (
              <NotFoundPage />
            )
          }
        />
        <Route
          path="/translate"
          element={
            <Suspense fallback={<PageLoader />}>
              <OnlineOnly feature="translate"><TranslatePage /></OnlineOnly>
            </Suspense>
          }
        />
        <Route
          path="/pt/translate"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <OnlineOnly feature="translate"><TranslatePage /></OnlineOnly>
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/speaking-practice"
          element={
            <Suspense fallback={<PageLoader />}>
              <FeatureGate featureId="speaking"><SpeakingPracticePage /></FeatureGate>
            </Suspense>
          }
        />
        <Route
          path="/pt/speaking-practice"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <FeatureGate featureId="speaking"><SpeakingPracticePage /></FeatureGate>
              </LanguageRoute>
            </Suspense>
          }
        />
        <Route
          path="/login"
          element={
            <Suspense fallback={<PageLoader />}>
              <RedirectIfAuthenticated>
                <LoginPage />
              </RedirectIfAuthenticated>
            </Suspense>
          }
        />
        <Route
          path="/pt/login"
          element={
            <Suspense fallback={<PageLoader />}>
              <RedirectIfAuthenticated>
                <LanguageRoute lang="pt">
                  <LoginPage />
                </LanguageRoute>
              </RedirectIfAuthenticated>
            </Suspense>
          }
        />
        <Route
          path="/auth/twitter/finish"
          element={
            // Deliberately NOT wrapped in RequireAuth or
            // RedirectIfAuthenticated - this page has to work both for a
            // not-yet-authenticated "Continue with X" sign-in AND for an
            // already-authenticated "Connect X" link from Profile (see
            // TwitterAuthFinishPage.tsx).
            <Suspense fallback={<PageLoader />}>
              <TwitterAuthFinishPage />
            </Suspense>
          }
        />
        <Route
          path="/register"
          element={
            <Suspense fallback={<PageLoader />}>
              <RedirectIfAuthenticated>
                <RegisterPage />
              </RedirectIfAuthenticated>
            </Suspense>
          }
        />
        <Route
          path="/pt/register"
          element={
            <Suspense fallback={<PageLoader />}>
              <RedirectIfAuthenticated>
                <LanguageRoute lang="pt">
                  <RegisterPage />
                </LanguageRoute>
              </RedirectIfAuthenticated>
            </Suspense>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <Suspense fallback={<PageLoader />}>
              <RedirectIfAuthenticated>
                <ForgotPasswordPage />
              </RedirectIfAuthenticated>
            </Suspense>
          }
        />
        <Route
          path="/pt/forgot-password"
          element={
            <Suspense fallback={<PageLoader />}>
              <RedirectIfAuthenticated>
                <LanguageRoute lang="pt">
                  <ForgotPasswordPage />
                </LanguageRoute>
              </RedirectIfAuthenticated>
            </Suspense>
          }
        />
        <Route
          path="/profile"
          element={
            <Suspense fallback={<PageLoader />}>
              <RequireAuth>
                <ProfilePage />
              </RequireAuth>
            </Suspense>
          }
        />
        <Route
          path="/pt/profile"
          element={
            <Suspense fallback={<PageLoader />}>
              <LanguageRoute lang="pt">
                <RequireAuth>
                  <ProfilePage />
                </RequireAuth>
              </LanguageRoute>
            </Suspense>
          }
        />
      </Routes>
    </>
  )
}

export default App
